import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../context/ThemeContext';
import {
  X,
  Fuel,
  AlertTriangle,
  MoreVertical,
  CreditCard,
} from 'lucide-react';
import '../styles/responsive/pages/PayoutRequests.css';
import '../styles/enterprise.css';
import { formatUSD, formatCrypto, formatCount, truncateHash } from '../utils/formatAmount';
import { fetchWithAuth } from '../utils/api';
import toast from 'react-hot-toast';
import useDebounce from '../hooks/useDebounce';

import { useNavigate, useSearchParams } from 'react-router-dom';

import PageHeaderPro from '../components/common/PageHeaderPro';
import KpiStrip from '../components/common/KpiStrip';
import KpiCard from '../components/common/KpiCard';
import FilterTabs from '../components/common/FilterTabs';
import Pager from '../components/common/Pager';
import StatusBadge from '../components/common/StatusBadge';
import EntSearch from '../components/common/EntSearch';
import { TableSkeleton } from '../components/common/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// An admin rejection is not a failed blockchain transaction, so 'rejected' keeps
// its own red "Rejected" pill; the rejection reason is surfaced via `detail`.
// 'failed' stays reserved for payouts that failed on chain.

// Read a field that the API may expose in either camelCase variant or snake_case.
const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

const PayoutRequests = () => {
  const { token } = useSelector((state) => state.auth);
  const { mode } = useTheme();

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    processingRequests: 0,
    heldRequests: 0,
    completedRequests: 0,
    rejectedRequests: 0,
    totalPaidOut: 0,
    pendingAmount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  // Newest-first by default, matching the merchant-side list. (Was ASC/FIFO.)
  // Priority still leads server-side — see findAllRequests.
  const [sortOrder, setSortOrder] = useState('DESC');
  // REQ-02: operator gas-tank balance, to warn before approving payouts.
  const [gasTank, setGasTank] = useState(null);

  const navigate = useNavigate();

  // Optional merchant scope: MerchantDetail's "Payout queue" button links here
  // with ?merchantId=<id>. When present, the queue is filtered to that merchant.
  const [searchParams, setSearchParams] = useSearchParams();
  const merchantIdFilter = searchParams.get('merchantId') || '';

  const fetchRequests = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder,
      });

      params.append('isTestnet', mode === 'test' ? 'true' : 'false');

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (debouncedSearchQuery) {
        params.append('search', debouncedSearchQuery);
      }
      if (merchantIdFilter) {
        params.append('merchantId', merchantIdFilter);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/all?${params}`);
      const data = await response.json();

      if (data.success) {
        setRequests(data.data.requests);
        setTotalPages(data.data.pagination.totalPages);
        setTotalItems(data.data.pagination.total);
      }
    } catch (error) {
      console.error('Failed to fetch payout requests:', error);
      toast.error('Failed to fetch payout requests');
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, pageSize, statusFilter, debouncedSearchQuery, sortBy, sortOrder, mode, merchantIdFilter]);

  const fetchStats = useCallback(async () => {
    if (!token) return;

    try {
      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/admin/stats?${params}`);
      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch payout stats:', error);
    }
  }, [token, mode]);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests, fetchStats]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  // Clear the merchant scope: drop the param (refetch is triggered by the
  // merchantIdFilter dependency change) and reset to the first page.
  const clearMerchantFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('merchantId');
    setSearchParams(next, { replace: true });
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const openPayout = (request) => {
    navigate(`/dashboard/payouts/${request.requestId}`, { state: { request } });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Plan-derived fee/net for rows (rows omit fee/net amounts).
  const rowFee = (request) => {
    const fee = pick(request, 'feeAmount', 'fee_amount');
    return fee !== undefined ? Number(fee) : null;
  };
  const rowNet = (request) => {
    // BUG-11: the native token value is the source of truth. Derive the net USD
    // from token_amount * conversion_rate when both are present; fall back to the
    // stored USD columns for older records that only have USD values.
    const tokenAmount = pick(request, 'tokenAmount', 'token_amount');
    const rate = pick(request, 'conversionRate', 'conversion_rate');
    if (tokenAmount != null && rate != null && Number(rate) > 0) {
      return Number(tokenAmount) * Number(rate);
    }
    const net = pick(request, 'netAmount', 'net_amount');
    if (net !== undefined) return Number(net);
    const fee = rowFee(request);
    return fee != null ? Number(request.amount) - fee : null;
  };

  // Surface rejection / hold / failure reason next to the status pill.
  const statusDetail = (request) => {
    if (request.status === 'rejected') {
      const r = pick(request, 'rejectionReason', 'rejection_reason');
      return r ? (r.length > 18 ? `${r.slice(0, 18)}…` : r) : undefined;
    }
    if (request.status === 'held') {
      const r = pick(request, 'heldReason', 'held_reason');
      return r ? (r.length > 18 ? `${r.slice(0, 18)}…` : r) : undefined;
    }
    return undefined;
  };

  const tabs = [
    { key: 'all', label: 'All', count: formatCount(stats.totalRequests) },
    { key: 'pending', label: 'Pending', count: formatCount(stats.pendingRequests) },
    { key: 'approved', label: 'Approved', count: formatCount(stats.approvedRequests || 0) },
    { key: 'held', label: 'Held', count: formatCount(stats.heldRequests || 0) },
    { key: 'processing', label: 'Processing', count: formatCount(stats.processingRequests || 0) },
    { key: 'completed', label: 'Completed', count: formatCount(stats.completedRequests) },
    { key: 'rejected', label: 'Rejected', count: formatCount(stats.rejectedRequests) },
  ];
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/wallets/gas-tank-balance`);
        const data = await response.json();
        if (active && data.success) setGasTank(data.data);
      } catch (error) {
        // Supplementary; ignore failures.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const lowGasNetworks = gasTank?.networks?.filter((n) => n.low === true) || [];

  // Label for the merchant-scope chip: prefer the merchant name from a loaded
  // row, fall back to the raw id from the URL.
  const merchantFilterLabel =
    requests.find((r) => String(r.merchantId) === String(merchantIdFilter))?.merchant?.name || merchantIdFilter;

  return (
    <div className="ent ent-page payout-requests-page">
      <PageHeaderPro
        title="Payout Requests"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={isLoading ? 'Updating…' : 'Updated just now'}
      />

      {gasTank?.configured && lowGasNetworks.length > 0 && (
        <div className="gas-tank-banner gas-warn">
          <AlertTriangle size={18} />
          <div>
            <strong>Low gas tank</strong>
            <span>
              {' '}Insufficient native balance to cover network fees on:{' '}
              {lowGasNetworks.map((n) => `${n.name} (${parseFloat(n.balance).toFixed(4)} ${n.nativeCurrency})`).join(', ')}.
              Top up before approving payouts on these networks.
            </span>
          </div>
        </div>
      )}
      {gasTank?.configured && lowGasNetworks.length === 0 && gasTank.networks?.length > 0 && (
        <div className="gas-tank-banner gas-ok">
          <Fuel size={16} />
          <span>Gas tank funded across {gasTank.networks.filter((n) => n.status === 'ok').length} network(s).</span>
        </div>
      )}

      <KpiStrip>
        <KpiCard
          label="PENDING"
          value={`$${formatUSD(stats.pendingAmount || 0)}`}
          delta={null}
          sub={`${formatCount(stats.pendingRequests)} awaiting review`}
        />
        <KpiCard
          label="LIFETIME PAID"
          value={`$${formatUSD(stats.totalPaidOut)}`}
          delta={null}
          sub={`${formatCount(stats.completedRequests)} payouts`}
        />
      </KpiStrip>

              <FilterTabs
          tabs={tabs}
          active={statusFilter}
          onChange={handleStatusFilter}
          right={
            <EntSearch
              value={searchQuery}
              onChange={(v) => {
                setSearchQuery(v);
                setCurrentPage(1);
              }}
              placeholder="Search by request ID or address…"
            />
          }
        />
      

      {merchantIdFilter && (
        <div className="merchant-filter-row">
          <span className="merchant-filter-chip">
            <span className="merchant-filter-chip__label">
              Filtered: <strong>{merchantFilterLabel}</strong>
            </span>
            <button
              type="button"
              className="merchant-filter-chip__clear"
              onClick={clearMerchantFilter}
              aria-label="Clear merchant filter"
              title="Clear merchant filter"
            >
              <X size={14} />
            </button>
          </span>
        </div>
      )}

            <div className="pro-table-wrap">
        {isLoading ? (
          <TableSkeleton rows={pageSize} cols={8} />
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <CreditCard size={48} />
            <p>No payout requests found</p>
            <span>Payout requests will appear here when merchants submit them</span>
          </div>
        ) : (
          <table className="pro-table">
            <thead>
              <tr>
                <th>REQUEST</th>
                <th>MERCHANT</th>
                <th className="amount-cell" onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  AMOUNT
                </th>
                <th className="amount-cell">NET</th>
                <th>DESTINATION</th>
                <th>STATUS</th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                  DATE
                </th>
                <th className="col-actions">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const fee = rowFee(request);
                const net = rowNet(request);
                return (
                  <tr key={request.id} onClick={() => openPayout(request)} style={{ cursor: 'pointer' }}>
                    <td data-label="REQUEST">
                      <span className="mono">{request.requestId}</span>
                      {request.isTestnet && <span className="testnet-badge">Testnet</span>}
                      {request.priority && <span className="priority-badge">Priority</span>}
                    </td>
                    <td data-label="MERCHANT">
                      <div className="cell-2">
                        <span className="cell-2__main">{request.merchant?.name || 'Unknown'}</span>
                        <span className="cell-2__sub">{request.merchant?.email}</span>
                      </div>
                    </td>
                    <td className="amount-cell" data-label="AMOUNT">
                      <span className="amount-cell__main">${formatUSD(request.amount)}</span>
                      {request.tokenAmount && (
                        <span className="amount-cell__sub">
                          {formatCrypto(parseFloat(request.tokenAmount))} {request.tokenSymbol}
                        </span>
                      )}
                    </td>
                    <td className="amount-cell" data-label="NET">
                      <span className="amount-cell__main">{net != null ? `$${formatUSD(net)}` : '—'}</span>
                      {fee != null && <span className="amount-cell__sub">fee ${formatUSD(fee)}</span>}
                    </td>
                    <td data-label="DESTINATION">
                      <div className="cell-2">
                        <span className="cell-2__main">{request.networkName || request.tokenSymbol || 'Wallet'}</span>
                        <span className="cell-2__sub mono">
                          {request.destinationAddress ? truncateHash(request.destinationAddress, 6, 6) : '—'}
                        </span>
                      </div>
                    </td>
                    <td data-label="STATUS">
                      <StatusBadge status={request.status} detail={statusDetail(request)} dot />
                    </td>
                    <td data-label="DATE">
                      <span className="cell-2__sub">{formatDate(request.createdAt)}</span>
                    </td>
                    <td className="col-actions" data-label="ACTIONS" onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions">
                        <button className="ent-btn ent-btn-secondary ent-btn--xs" onClick={() => openPayout(request)}>
                          View
                        </button>
                        <button
                          className="row-kebab"
                          onClick={() => navigate(`/dashboard/merchants/${request.merchantId}`)}
                          aria-label="Merchant profile"
                          title="Merchant profile"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {requests.length > 0 && (
          <Pager page={currentPage} pageCount={totalPages} total={totalItems} pageSize={pageSize} onPage={setCurrentPage} />
        )}
      </div>
      

      <style>{`
        .ent.payout-requests-page { padding: 24px; }

        .ent .kpi-strip { margin: 16px 0; }
        .ent .filter-tabs { margin-bottom: 14px; }

        .ent .pro-table-wrap { margin-bottom: 16px; }

        /* Merchant-scope filter chip */
        .ent .merchant-filter-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
        .ent .merchant-filter-chip {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 8px 6px 12px;
          background: var(--accent-soft, var(--ent-card-alt));
          border: 1px solid var(--ent-border-strong);
          border-radius: 999px;
          font-size: 0.8rem; color: var(--ent-text);
          max-width: 100%;
        }
        .ent .merchant-filter-chip__label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ent .merchant-filter-chip__label strong { color: var(--ent-text); font-weight: 600; }
        .ent .merchant-filter-chip__clear {
          display: inline-flex; align-items: center; justify-content: center;
          width: 20px; height: 20px; padding: 0;
          background: transparent; border: none; border-radius: 50%;
          color: var(--ent-muted); cursor: pointer; flex-shrink: 0;
        }
        .ent .merchant-filter-chip__clear:hover { background: var(--ent-card); color: var(--ent-text); }

        /* Gas tank banners */
        .ent .gas-tank-banner {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          padding: 0.75rem 1rem;
          border-radius: var(--ent-radius);
          margin-bottom: 1rem;
          font-size: 0.875rem;
          line-height: 1.4;
        }
        .ent .gas-tank-banner.gas-warn {
          background: var(--ent-amber-bg);
          border: 1px solid var(--ent-amber);
          color: var(--ent-amber);
        }
        .ent .gas-tank-banner.gas-ok {
          background: var(--ent-green-bg, rgba(16,185,129,0.1));
          border: 1px solid var(--ent-green, #10b981);
          color: var(--ent-green, #047857);
          align-items: center;
        }

        /* testnet chip */
        .ent .pro-table .testnet-badge {
          display: inline-block;
          padding: 0.125rem 0.375rem;
          background: var(--ent-amber-bg);
          color: var(--ent-amber);
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        /* P3: priority chip */
        .ent .pro-table .priority-badge {
          display: inline-block;
          padding: 0.125rem 0.375rem;
          background: var(--brand-100, rgba(124, 58, 237, 0.12));
          color: var(--brand-700, #6d28d9);
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        /* Row actions */
        .ent .col-actions { width: 1%; white-space: nowrap; }
        .ent .row-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
        .ent .ent-btn--xs { height: 28px; padding: 0 10px; font-size: 12px; }
        .ent .ent-btn-danger {
          background: var(--ent-red, #ef4444);
          color: #fff;
        }
        .ent .ent-btn-danger:hover:not(:disabled) { background: #dc2626; }

        /* Loading & empty */
        .ent .loading-container {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 3rem; gap: 1rem; color: var(--ent-muted);
        }
        .ent .loading-spinner {
          width: 32px; height: 32px;
          border: 3px solid var(--ent-border);
          border-top-color: var(--ent-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .ent .empty-state {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 3rem; color: var(--ent-muted); gap: 0.25rem;
        }
        .ent .empty-state svg { color: var(--ent-muted); margin-bottom: 0.5rem; }
        .ent .empty-state p { margin: 0; font-weight: 600; color: var(--ent-text); }
        .spinning { animation: spin 1s linear infinite; }

        /* Drawer */
        .drawer-overlay {
          position: fixed; inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }
        .request-detail-drawer {
          position: fixed; top: 0; right: 0;
          width: 480px; max-width: 100%; height: 100vh;
          background: var(--ent-bg, var(--ent-card));
          z-index: 1000;
          display: flex; flex-direction: column;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .drawer-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 1rem;
          background: var(--ent-card);
          border-bottom: 1px solid var(--ent-border);
        }
        .drawer-header-left { display: flex; align-items: center; gap: 1rem; }
        .close-btn {
          background: transparent; border: none; color: var(--ent-muted);
          cursor: pointer; padding: 0.5rem; border-radius: 6px;
        }
        .close-btn:hover { background: var(--ent-card-alt); color: var(--ent-text); }
        .drawer-title-section { display: flex; align-items: center; gap: 0.5rem; }
        .drawer-title-section h2 {
          margin: 0; font-size: 1rem; font-weight: 600;
          color: var(--ent-text); font-family: monospace;
        }
        .drawer-content { flex: 1; overflow-y: auto; padding: 1rem; background: var(--ent-card-alt); }

        .detail-card {
          background: var(--ent-card);
          border: 1px solid var(--ent-border);
          border-radius: var(--ent-radius);
          padding: 1rem; margin-bottom: 1rem;
        }
        .detail-card h3 { margin: 0 0 1rem 0; font-size: 0.9rem; font-weight: 600; color: var(--ent-text); }
        .detail-card p { margin: 0; font-size: 0.875rem; color: var(--ent-text-2); line-height: 1.5; }
        .rejection-card { border-color: var(--ent-red, #ef4444); background: rgba(239,68,68,0.05); }
        .rejection-card h3 { color: var(--ent-red, #ef4444); }
        .held-card { border-color: var(--ent-amber); background: var(--ent-amber-bg); }
        .held-card h3 { color: var(--ent-amber); }

        .amount-display { display: flex; flex-direction: column; gap: 0.35rem; }
        .amount-large { font-size: 1.75rem; font-weight: 700; color: var(--ent-text); font-variant-numeric: tabular-nums; }
        .token-amount { font-size: 1rem; color: var(--ent-text-2); }
        .amount-breakdown { display: flex; gap: 1.25rem; font-size: 0.8rem; color: var(--ent-muted); margin-top: 2px; }
        .amount-breakdown strong { color: var(--ent-text); }
        .conversion-rate { font-size: 0.8rem; color: var(--ent-muted); }

        .detail-info-grid { display: flex; flex-direction: column; gap: 0.75rem; }
        .detail-info-item { display: flex; align-items: flex-start; gap: 0.75rem; }
        .detail-info-item svg, .icon-placeholder {
          color: var(--ent-muted); margin-top: 0.125rem; flex-shrink: 0; width: 16px; text-align: center;
        }
        .detail-info-item label { display: block; font-size: 0.7rem; color: var(--ent-muted); margin-bottom: 0.125rem; }
        .detail-info-item span { font-size: 0.875rem; color: var(--ent-text); }
        .address-full { word-break: break-all; font-family: monospace; font-size: 0.8rem !important; }
        .tx-hash-link { color: var(--ent-primary); text-decoration: none; word-break: break-all; font-size: 0.85rem; }
        .tx-hash-link:hover { text-decoration: underline; }

        .wallet-status-card { border-color: var(--accent); background: var(--accent-soft); }
        .wallet-connected { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
        .wallet-address { display: flex; align-items: center; gap: 0.5rem; font-family: monospace; font-size: 0.875rem; color: var(--ent-text); }
        .wallet-not-connected { display: flex; flex-direction: column; align-items: flex-start; gap: 0.75rem; }
        .wallet-not-connected span { font-size: 0.8rem; color: var(--ent-text-2); }
        .btn-disconnect {
          padding: 0.375rem 0.75rem; background: transparent;
          border: 1px solid var(--ent-border-strong); border-radius: 6px;
          font-size: 0.75rem; color: var(--ent-muted); cursor: pointer;
        }
        .btn-disconnect:hover { border-color: var(--ent-red, #ef4444); color: var(--ent-red, #ef4444); }

        .drawer-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.5rem; }
        .drawer-actions .ent-btn { flex: 1 1 auto; min-width: 120px; }

        .already-processed-notice {
          display: flex; align-items: flex-start; gap: 0.75rem; padding: 1rem;
          background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: var(--ent-radius); width: 100%;
        }
        .already-processed-notice svg { color: #10b981; flex-shrink: 0; margin-top: 0.125rem; }
        .already-processed-notice > div { display: flex; flex-direction: column; gap: 0.25rem; }
        .already-processed-notice .notice-title { font-weight: 600; color: #10b981; font-size: 0.9rem; }
        .already-processed-notice .notice-text { font-size: 0.8rem; color: var(--ent-text-2); }

        /* Modal */
        .modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); z-index: 1001; }
        .action-modal {
          position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 440px; max-width: 92%;
          background: var(--ent-card);
          border: 1px solid var(--ent-border);
          border-radius: var(--ent-radius);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
          z-index: 1002;
        }
        .modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 1rem; border-bottom: 1px solid var(--ent-border);
        }
        .modal-header h3 { margin: 0; font-size: 1rem; font-weight: 600; color: var(--ent-text); }
        .modal-content { padding: 1rem; }
        .modal-note { margin: 0 0 1rem 0; font-size: 0.8rem; color: var(--ent-text-2); line-height: 1.5; }
        .form-group { margin-bottom: 1rem; }
        .form-group:last-child { margin-bottom: 0; }
        .form-group label { display: block; font-size: 0.8rem; font-weight: 500; color: var(--ent-text-2); margin-bottom: 0.5rem; }
        .form-group input, .form-group textarea {
          width: 100%; padding: 0.625rem;
          border: 1px solid var(--ent-border-strong); border-radius: 8px;
          font-size: 0.875rem; background: var(--ent-card-alt); color: var(--ent-text);
          resize: vertical; font-family: inherit;
        }
        .form-group input:focus, .form-group textarea:focus { outline: none; border-color: var(--ent-primary); }
        .modal-footer {
          display: flex; justify-content: flex-end; gap: 0.75rem;
          padding: 1rem; border-top: 1px solid var(--ent-border);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .ent.payout-requests-page { padding: 16px; }
          .request-detail-drawer { width: 100%; }
          .drawer-actions .ent-btn { flex: 1 1 100%; }
        }
        @media (max-width: 576px) {
          .ent .pro-table .col-actions { text-align: right; }
          .ent .row-actions { justify-content: flex-end; flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
};

export default PayoutRequests;
